import { Navigate, useParams } from "@tanstack/react-router";
import React, { useMemo, useState } from "react";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { ChoiceCard, YesNo } from "@patient-watch/forms";
import { inputs as i } from "@/components/input";
import MsAdlSelection from "@/components/input/MsAdlSelection";
import MsAdlRatingsList from "@/components/input/MsAdlRatingsList";
import RatingsList from "@/components/input/RatingsList";
import RepeatableGroup from "./RepeatableGroup";
import DocumentUploadInput from "./DocumentUploadInput";
import { BranchingScaleOptionsInput } from "@/components/input/custom/branching-scale-options/BranchingScaleOptionsInput";
import { Route as FootPhotos } from "@/routes/_app/diaries/$diaryId/new/tracker/$trackerId/entry/$entryId/page/$pageIndex/foot-photos/start";
import { services as s } from "@/services";
import type { InputT } from "@patient-watch/types";
import { resolveInputKey } from "@/features/pages/utils/inputKeyAliases";
import { useEntryValuesStore } from "../hooks/useEntryValuesStore";
import AddressAutocompleteInput from "./AddressAutocompleteInput";
import MeasurementInput from "./MeasurementInput";
import CountInput from "./CountInput";
import { isMeasurementUnit } from "../measurement";
import { DictionaryField } from "@/features/dictionaries/components/DictionaryField";
import { resolveInputDictionary, withResolvedChoices } from "@/features/dictionaries/inputChoices";
import { withOwnOptions } from "@/features/dictionaries/ownOptions";
import { useAddOwnOption } from "@/features/dictionaries/useAddOwnOption";
import { useViewersOwnOptions } from "@/features/dictionaries/useOwnOptionsStore";
import { useAuth } from "@/utils/auth";
import { useInputWithChoices } from "@/features/dictionaries/useInputsWithChoices";
import { useLocale } from "@/features/i18n/LocaleProvider";
import { useFormPageCopy } from "@/features/i18n/formPageCopy";

/** Inputs whose caption is drawn by their card (ChoiceCard or a choice component), not above the control. */
const CAPTION_IN_CARD_KEYS = new Set<string>([
    "checkbox",
    "dropdown",
    "multiple-choice",
    "scale-rating",
    "single-choice",
    "slider",
    "star",
    "yes-no",
]);

interface Props {
    is_preview?: boolean;
    input: InputT;
    form: Record<string, any>; // Form object from react-hook-form
    msAdlSelectionInputId?: string;
    previewContext?: {
        entryId: string;
        entryValueId?: string;
        diaryId?: string;
        trackerId?: string;
        pageIndex?: string;
    };
}

const InputSwitch = ({ input: inputWithoutRows, is_preview, form, msAdlSelectionInputId, previewContext }: Props) => {
    const locale = useLocale();
    const formPageCopy = useFormPageCopy();
    const routeParams = useParams({
        strict: false,
    }) as {
        entryId?: string;
        entryValueId?: string;
        diaryId?: string;
        trackerId?: string;
        pageIndex?: string;
    };
    const {
        entryId = previewContext?.entryId,
        entryValueId: paramEntryValueId = previewContext?.entryValueId,
        diaryId = previewContext?.diaryId,
        trackerId = previewContext?.trackerId,
        pageIndex = previewContext?.pageIndex,
    } = routeParams;
    const upsertEntryValue = useEntryValuesStore((state) => state.upsertEntryValue);
    const entryValue = useEntryValuesStore(
        (state) => state.entry_values.find((ev) => ev.input_id === inputWithoutRows.input_id) ?? null
    );
    const { id: userId } = useAuth();
    const knownOwnOptions = useViewersOwnOptions({ userId, entryId });
    // Loaders that skip pages_view (the form builder) give a dictionary_id with no rows. Fetch them here.
    const inputWithRows = useInputWithChoices(inputWithoutRows);
    // Other respondents' own options are not offered; this respondent's are, and so is anything already picked.
    const inputFromForm = useMemo(
        () =>
            withOwnOptions(
                inputWithRows,
                { userId, entryId },
                entryValue?.text_array ?? (entryValue?.text_value ? [entryValue.text_value] : []),
                knownOwnOptions
            ),
        [inputWithRows, userId, entryId, entryValue?.text_array, entryValue?.text_value, knownOwnOptions]
    );
    // An input bound to a dictionary arrives with `dictionary` and `option_rows`. Fill scale_options and
    // options from them once, so every input below reads its choices the way it always has.
    const input = useMemo(() => withResolvedChoices(inputFromForm, locale), [inputFromForm, locale]);
    const dictionary = useMemo(() => resolveInputDictionary(inputFromForm, locale), [inputFromForm, locale]);
    // A preview never writes options to the database.
    const addOwnOption = useAddOwnOption(inputFromForm, { userId, entryId }, { saveToDatabase: !is_preview });
    // Use state to hold the generated ID if needed, ensuring it's generated only once
    const [localEntryValueId] = useState(() => paramEntryValueId ?? s.uuidv4());

    React.useEffect(() => {
        if (!entryId) return;

        const existingEntryValue = useEntryValuesStore.getState().getEntryValueByInputId(input.input_id);

        // Only upsert if it doesn't exist in the store yet
        if (!existingEntryValue) {
            upsertEntryValue({
                entry_id: entryId,
                input_id: input.input_id,
                entry_value_id: localEntryValueId, // Use the stable, potentially generated ID
                // Initialize other fields to null or default as appropriate
                number_value: null,
                text_value: null,
                text_array: null,
                json_value: null,
                object_id: null,
            });
        }
        // Depend only on stable identifiers necessary for initialization
    }, [entryId, input.input_id, localEntryValueId, upsertEntryValue]);

    // Options drawn by a shared control with no card of its own sit in the same card as the rest.
    // The control keeps its own label and description, so the card only adds the caption.
    const inCard = (control: React.ReactNode) => <ChoiceCard caption={input.input_caption}>{control}</ChoiceCard>;

    const content = (() => {
        switch (resolveInputKey(input.input_key)) {
            case "address-autocomplete":
                return <AddressAutocompleteInput input={input} form={form} />;
            case "short-text":
                return (
                    <fc.Input
                        name={input.input_id}
                        label={input.input_label ?? undefined}
                        description={input.input_description ?? undefined}
                        required={!input.is_optional}
                    />
                );
            case "long-text":
                return (
                    <fc.Textarea
                        name={input.input_id}
                        label={input.input_label ?? undefined}
                        description={input.input_description ?? undefined}
                        required={!input.is_optional}
                    />
                );
            case "date-picker":
                return (
                    <fc.Date
                        name={input.input_id}
                        label={input.input_label ?? undefined}
                        description={input.input_description ?? undefined}
                        required={!input.is_optional}
                    />
                );
            case "date-time-picker":
                return (
                    <fc.DateTime
                        name={input.input_id}
                        label={input.input_label ?? undefined}
                        description={input.input_description ?? undefined}
                        required={!input.is_optional}
                    />
                );
            case "number":
                return (
                    <i.Number
                        inputId={input.input_id}
                        value={entryValue?.number_value?.toString() ?? ""}
                        setValue={(val: string) => {
                            const numValue = val === "" ? null : Number.parseFloat(val);
                            // Update the store
                            upsertEntryValue({
                                entry_id: entryId,
                                input_id: input.input_id,
                                entry_value_id: localEntryValueId,
                                number_value: numValue,
                            });
                            // Also update react-hook-form so the value is included on submit. Answers are only
                            // checked when Next is pressed (useFormPage), so no validation here.
                            form.setValue(input.input_id, numValue);
                        }}
                        label={input.input_label ?? undefined}
                        description={
                            input.input_description ?? (input.validation_message ? input.validation_message : undefined)
                        }
                        // A study question with its own range message (inputs.validation_message) shows that message
                        // inline when the typed value is out of range, and keeps the value so Next can check it. The
                        // plus and minus buttons still stop at the range.
                        validationMessage={input.validation_message}
                        unit={input.unit}
                        decimalPoints={input.decimal_points}
                        step={input.step}
                        required={!input.is_optional}
                        min={input.min_value}
                        max={input.max_value}
                    />
                );
            case "measurement": {
                const allowedUnits = (input.options ?? []).filter(isMeasurementUnit);
                const unit = isMeasurementUnit(entryValue?.text_value)
                    ? entryValue.text_value
                    : (allowedUnits[0] ?? "cm");
                return (
                    <MeasurementInput
                        name={input.input_id}
                        label={input.input_label ?? "Measurement"}
                        description={input.input_description ?? undefined}
                        required={!input.is_optional}
                        allowedUnits={allowedUnits.length > 0 ? allowedUnits : [unit]}
                        min={input.min_value}
                        max={input.max_value}
                        step={input.step}
                        onStoreChange={(value) => {
                            if (!entryId) return;
                            upsertEntryValue({
                                entry_id: entryId,
                                input_id: input.input_id,
                                entry_value_id: localEntryValueId,
                                number_value: value.value,
                                text_value: value.unit,
                            });
                        }}
                    />
                );
            }
            case "count":
                return (
                    <CountInput
                        input={input}
                        onStoreChange={(value) => {
                            if (!entryId) return;
                            upsertEntryValue({
                                entry_id: entryId,
                                input_id: input.input_id,
                                entry_value_id: localEntryValueId,
                                number_value: value,
                            });
                        }}
                    />
                );
            case "yes-no":
                return (
                    <YesNo
                        name={input.input_id}
                        label={input.input_label ?? ""}
                        caption={input.input_caption ?? undefined}
                        description={input.input_description ?? undefined}
                        required={!input.is_optional}
                        yesLabel={formPageCopy.yes}
                        noLabel={formPageCopy.no}
                    />
                );
            case "checkbox":
                return inCard(
                    <fc.Checkbox
                        name={input.input_id}
                        label={input.input_label ?? ""}
                        description={input.input_description ?? undefined}
                        required={!input.is_optional}
                    />
                );
            case "dropdown":
                if (dictionary) {
                    // Control, copy, groups and icons come from the dictionary. A dropdown always takes one answer.
                    return inCard(
                        <DictionaryField
                            name={input.input_id}
                            dictionary={dictionary}
                            locale={locale}
                            control={dictionary.isSearchable ? "combobox" : "select"}
                            label={input.input_label ?? "Select"}
                            description={input.input_description ?? undefined}
                            required={!input.is_optional}
                            ownOptions={addOwnOption}
                        />
                    );
                }
                return inCard(
                    <fc.Select
                        options={
                            input.scale_options?.length
                                ? input.scale_options.map((o) => ({
                                      label: o.label,
                                      value: String(o.value),
                                  }))
                                : (input.options?.map((o) => ({
                                      label: o,
                                      value: o,
                                  })) ?? [])
                        }
                        name={input.input_id}
                        label={input.input_label ?? "Select"}
                        required={!input.is_optional}
                        ownOptions={addOwnOption}
                    />
                );
            case "duration":
                return <i.Duration form={form} input={input} />;
            case "email":
                return <fc.Email name={input.input_id} />;
            case "slider":
                return (
                    <i.Slider
                        setValue={(val: number | null) => {
                            // Update the store
                            upsertEntryValue({
                                entry_id: entryId,
                                input_id: input.input_id,
                                entry_value_id: localEntryValueId,
                                number_value: val,
                            });
                            // Also update react-hook-form so the value is included on submit
                            form.setValue(input.input_id, val);
                        }}
                        value={entryValue?.number_value ?? 0}
                        input={input}
                        label={input.input_label ?? ""}
                    />
                );
            case "single-choice":
                return <fc.SingleChoice input={input} addOwnOption={addOwnOption} />;
            case "scale-rating":
                return <fc.Rating input={input} name={input.input_id} label={input.input_label ?? ""} />;
            case "star":
                return inCard(<fc.Star input={input} name={input.input_id} label={input.input_label ?? ""} />);
            case "multiple-choice":
                if (dictionary?.isSearchable) {
                    return inCard(
                        <DictionaryField
                            name={input.input_id}
                            dictionary={dictionary}
                            locale={locale}
                            control="multiple"
                            label={input.input_label ?? undefined}
                            description={input.input_description ?? undefined}
                            required={!input.is_optional}
                        />
                    );
                }
                return <i.MultipleChoice input={input} addOwnOption={addOwnOption} />;
            case "photo":
                return <i.Photo input={input} />;
            case "file":
                return (
                    <DocumentUploadInput
                        name={input.input_id}
                        label={input.input_label ?? ""}
                        description={input.input_description ?? undefined}
                        required={!input.is_optional}
                        diaryId={diaryId}
                        entryId={entryId}
                        inputId={input.input_id}
                    />
                );
            case "foot-photos":
                return is_preview ? (
                    <p>No Preview Available</p>
                ) : (
                    Navigate({
                        to: FootPhotos.to,
                        params: {
                            diaryId: diaryId as string,
                            trackerId: trackerId as string,
                            entryId: entryId,
                            pageIndex: pageIndex as string,
                        },
                    })
                );

            case "month-year":
                return (
                    <fc.MonthYear name={input.input_id} label={input.input_label ?? ""} required={!input.is_optional} />
                );

            case "repeatable-group":
                if (input.input_list && input.input_list.length > 0) {
                    return (
                        <RepeatableGroup
                            name={input.input_id}
                            label={input.input_label ?? ""}
                            description={input.input_description ?? undefined}
                            inputList={input.input_list}
                            required={!input.is_optional}
                        />
                    );
                }
                return null;

            // Custom Inputs
            case "pain-slider":
                return (
                    <i.ci.PainSlider
                        name={input.input_id}
                        label={input.input_label ?? undefined}
                        required={!input.is_optional}
                    />
                );
            case "glucose-slider":
                return (
                    <i.ci.GlucoseSlider
                        name={input.input_id}
                        label={input.input_label ?? undefined}
                        required={!input.is_optional}
                    />
                );
            case "visa-a-8":
            case "visa-g-8":
            case "visa-p-8":
            case "visa-h-8":
                return <BranchingScaleOptionsInput input={input} />;
            case "tried-treatment":
                return (
                    <i.ci.TriedTreatment
                        name={input.input_id}
                        label={input.input_label ?? ""}
                        required={!input.is_optional}
                    />
                );
            case "ratings-list":
                return (
                    <RatingsList
                        input={input}
                        name={input.input_id}
                        label={input.input_label ?? ""}
                        description={input.input_description ?? undefined}
                    />
                );
            case "ms-adl-ratings-list":
                return (
                    <MsAdlRatingsList
                        input={input}
                        name={input.input_id}
                        label={input.input_label ?? ""}
                        description={input.input_description ?? undefined}
                        selectionInputId={msAdlSelectionInputId}
                    />
                );
            case "ms-adl-selection":
                return (
                    <MsAdlSelection
                        input={input}
                        name={input.input_id}
                        label={input.input_label ?? ""}
                        description={input.input_description ?? undefined}
                    />
                );
        }
    })();

    if (!content) return null;

    // These input types render input_caption inside their own card/components.
    const captionRenderedInternally = CAPTION_IN_CARD_KEYS.has(resolveInputKey(input.input_key));
    if (input.input_caption && !captionRenderedInternally) {
        return (
            <div className="space-y-1">
                <p className="text-muted-foreground text-lg italic">{input.input_caption}</p>
                {content}
            </div>
        );
    }

    return content;
};

export default InputSwitch;
